import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from '../api/client';
import { setOnUnauthorized } from '../api/gate';
import { authResponseSchema, profileSchema, type Player } from '../api/types';
import { getInitData, initTelegram, isTelegram } from '../telegram';

type Status = 'loading' | 'login' | 'link-telegram' | 'terms' | 'onboarding' | 'ready' | 'error';

interface AuthActions {
  /** Merge partial player fields (after profile/avatar edits). */
  patchPlayer: (patch: Partial<Player>) => void;
  /** Set the session from an OTP / Telegram-Widget login result. */
  loginWithToken: (token: string, player: Player) => void;
  /** Re-run the gate ladder with a fresh player (после привязки Telegram). */
  applyPlayer: (player: Player) => void;
  /** Mark the public offer accepted; next gate is onboarding (new players). */
  markTermsAccepted: () => void;
  /** Mark first-run onboarding finished and enter the app. */
  markOnboarded: () => void;
  /** Clear the local session (re-auths inside Telegram, else returns to login). */
  logout: () => void;
}

interface AuthState extends AuthActions {
  status: Status;
  player: Player | null;
  error?: string;
}

const noop = () => {};
const AuthContext = createContext<AuthState>({
  status: 'loading',
  player: null,
  patchPlayer: noop,
  loginWithToken: noop,
  applyPlayer: noop,
  markTermsAccepted: noop,
  markOnboarded: noop,
  logout: noop,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: Status; player: Player | null; error?: string }>({
    status: 'loading',
    player: null,
  });
  const [nonce, setNonce] = useState(0);

  /** After any successful login: привязка Telegram → оферта → онбординг → приложение.
   *  Telegram обязателен (решение клуба): регистрация через веб (email OTP) не
   *  пройдёт дальше гейта привязки — сервер и так отдаёт 403 на всё, кроме /me. */
  const settle = useCallback((player: Player) => {
    const status = !player.telegramLinked
      ? 'link-telegram'
      : !player.termsAccepted
        ? 'terms'
        : !player.onboardingCompleted
          ? 'onboarding'
          : 'ready';
    setState({ status, player });
  }, []);

  useEffect(() => {
    let cancelled = false;
    initTelegram();

    // Outside Telegram (browser): restore a persisted session from the stored
    // token (email OR Telegram-widget login both persist here) so the user isn't
    // asked to log in again every visit. No token / invalid → show login.
    if (!isTelegram) {
      if (!getToken()) {
        setState({ status: 'login', player: null });
        return;
      }
      (async () => {
        try {
          const me = await api.get('/me', profileSchema);
          if (cancelled) return;
          settle({
            id: me.id,
            displayName: me.displayName,
            nickname: me.nickname,
            photoUrl: me.photoUrl,
            telegramUserId: null,
            telegramLinked: me.telegramLinked,
            termsAccepted: me.termsAccepted,
            onboardingCompleted: me.onboardingCompleted,
            isNew: false,
          });
        } catch {
          if (cancelled) return;
          setToken(null); // expired/invalid token → fall back to login
          setState({ status: 'login', player: null });
        }
      })();
      return;
    }

    // Inside Telegram → auto-authenticate via initData.
    (async () => {
      try {
        const res = await api.post('/auth/telegram', authResponseSchema, {
          initData: getInitData(),
        });
        if (cancelled) return;
        setToken(res.token);
        settle(res.player);
      } catch (err) {
        if (cancelled) return;
        setToken(null);
        setState({
          status: 'error',
          player: null,
          error: err instanceof Error ? err.message : 'Auth failed',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce, settle]);

  const patchPlayer = useCallback((patch: Partial<Player>) => {
    setState((s) => (s.player ? { ...s, player: { ...s.player, ...patch } } : s));
  }, []);

  const loginWithToken = useCallback(
    (token: string, player: Player) => {
      setToken(token);
      settle(player);
    },
    [settle],
  );

  const markTermsAccepted = useCallback(() => {
    setState((s) => {
      if (!s.player) return s;
      const player = { ...s.player, termsAccepted: true };
      return { status: player.onboardingCompleted ? 'ready' : 'onboarding', player };
    });
  }, []);

  const markOnboarded = useCallback(() => {
    setState((s) =>
      s.player ? { status: 'ready', player: { ...s.player, onboardingCompleted: true } } : s,
    );
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    if (isTelegram) {
      setState({ status: 'loading', player: null });
      setNonce((n) => n + 1); // re-auth from initData
    } else {
      setState({ status: 'login', player: null });
    }
  }, []);

  // 401 посреди сессии (протухший токен): клиент чистит токен и зовёт сюда —
  // в Telegram это тихий re-auth, в браузере возврат на экран входа.
  useEffect(() => {
    setOnUnauthorized(logout);
    return () => setOnUnauthorized(null);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        patchPlayer,
        loginWithToken,
        applyPlayer: settle,
        markTermsAccepted,
        markOnboarded,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
